(ns hai.entity.staging
  "Optimistic chips for the entity-extras sections: a picked tag / URL /
   contact / linked note / linked task shows immediately while its save is in
   flight, and a removed one hides immediately while its detach is. Chips are
   staged (and removals marked) per scope — the editing entity's uuid, or a
   host's session key when the entity may not exist yet — and family (:tags
   :links :contacts :notes :tasks). Hosts merge both into the rendered rows
   with [[merge-staged]] / [[merge-staged-tags]] and retire them when the
   mutation's response, which then carries the change, lands or fails. The
   edits live beside the entity's cached copy rather than in it, so a
   concurrent save's whole-entity re-store can't blank them."
  (:require
   [clojure.string :as str]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

(defn tag-chip
  "The chip shape a picked tag shows as while its save is in flight. A tag
   typed fresh has no uuid until the server makes one; its name carries the
   chip until the response reconciles."
  [tag]
  (cond-> {:group/uuid (:group/uuid tag)
           :group/display-name (or (:group/display-name tag) (:tag/name tag))}
    (:tag/name tag) (assoc :tag/name (:tag/name tag))))

(defn contact-chip
  [contact]
  (select-keys contact [:contact/uuid :contact/full-name :contact/organisation?]))

(defn note-chip
  [note]
  (select-keys note [:note/uuid :note/text]))

(defn task-chip
  [task]
  (select-keys task [:task/uuid :task/title]))

(defn stage
  [db scope family chip]
  (update-in db [::chips scope family] (fnil conj []) chip))

(defn unstage
  [db scope family chip]
  (update-in db [::chips scope family] (fn [chips] (vec (remove #{chip} chips)))))

(defn mark-removal
  "Mark one chip — by its id (uuid, url, …) — as being detached, hiding it
   until the detach's response lands or fails."
  [db scope family id]
  (update-in db [::removals scope family] (fnil conj #{}) id))

(defn unmark-removal
  [db scope family id]
  (update-in db [::removals scope family] (fnil disj #{}) id))

(defn clear
  "Drop every staged chip and removal mark under `scope` — for hosts resetting
   an editing session."
  [db scope]
  (-> db
      (update ::chips dissoc scope)
      (update ::removals dissoc scope)))

(defn staged
  [db scope family]
  (get-in db [::chips scope family] []))

(defn staged-map
  [db scope]
  (get-in db [::chips scope] {}))

(defn removals
  [db scope family]
  (get-in db [::removals scope family] #{}))

(defn removals-map
  [db scope]
  (get-in db [::removals scope] {}))

(defevent ::unstage [db scope family chip]
  (unstage db scope family chip))

(defevent ::unmark-removal [db scope family id]
  (unmark-removal db scope family id))

(defevent-fx ::add-failed [_ scope family chip error-text]
  {:fx [[:dispatch [::unstage scope family chip]]
        [:dispatch [:toast/error {:text error-text}]]]})

(defevent-fx ::remove-failed [_ scope family id error-text]
  {:fx [[:dispatch [::unmark-removal scope family id]]
        [:dispatch [:toast/error {:text error-text}]]]})

(defsub ::staged [db scope family]
  (staged db scope family))

(defsub ::staged-map [db scope]
  (staged-map db scope))

(defsub ::removals [db scope family]
  (removals db scope family))

(defsub ::removals-map [db scope]
  (removals-map db scope))

(defn merge-staged
  "`items` minus the ids marked for removal, plus the staged chips not already
   among them, compared by `id-attr` (`identity` for plain values such as
   URLs)."
  ([items id-attr staged]
   (merge-staged items id-attr staged #{}))
  ([items id-attr staged removed]
   (let [items (remove (comp removed id-attr) items)
         present (into #{} (keep id-attr) items)]
     (into (vec items) (remove (comp present id-attr)) staged))))

(defn merge-staged-tags
  "Tags minus the uuids marked for removal, plus the staged tag chips not
   already among them. A staged chip still awaiting its uuid matches a
   committed tag by display name, so the response reconciling a fresh tag
   never doubles its chip."
  ([tags staged]
   (merge-staged-tags tags staged #{}))
  ([tags staged removed]
   (let [tags (remove (comp removed :group/uuid) tags)
         present (into #{}
                       (mapcat (fn [tag]
                                 (cond-> []
                                   (:group/uuid tag)
                                   (conj (:group/uuid tag))
                                   (:group/display-name tag)
                                   (conj (str/lower-case (:group/display-name tag))))))
                       tags)]
     (into (vec tags)
           (remove (fn [chip]
                     (or (contains? present (:group/uuid chip))
                         (contains? present (some-> (:group/display-name chip)
                                                    str/lower-case)))))
           staged))))
